import { Button, Divider, Typography } from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import ResponsiveModal from "src/components/ResponsiveModal";

type EarlyPaymentInfoProps = {
  /** Advance fee percent (normal). */
  feePercent?: number;
  /** Advance percent of income. */
  percent?: number;
  /** Discounted fee for the user's first early payment, when promoted. */
  firstFeePercent?: number | null;
  /** Whether the user still qualifies for the discounted first early payment. */
  firstEligible?: boolean;
};

/**
 * Explanatory content for the early-payment feature — the flow and the rules.
 * Reused inside the finance-page info modal and the withdrawal-page sidebar.
 */
export function EarlyPaymentInfo({
  feePercent = 0,
  percent = 0,
  firstFeePercent = null,
  firstEligible = false,
}: EarlyPaymentInfoProps) {
  const showFirstOffer = firstEligible && firstFeePercent != null;

  return (
    <div className="text-right">
      <Typography.Paragraph className="text-sm">
        <ThunderboltOutlined className="ml-1 text-amber-500" />
        <strong>پرداخت زودهنگام</strong> به شما امکان می‌دهد بخشی از درآمد آینده‌ی کانال خود را
        زودتر دریافت کنید — مانند یک پیش‌دریافت کوتاه‌مدت که از درآمد ماه‌های بعد تسویه می‌شود.
      </Typography.Paragraph>

      <Divider className="my-3" orientation="right" plain>
        <span className="text-xs text-gray-500">چطور کار می‌کند؟</span>
      </Divider>

      <ol className="space-y-2 text-sm pr-4 list-decimal" style={{ paddingInlineStart: 18 }}>
        <li>
          هنگام برداشت، اگر مبلغ درخواستی بیشتر از موجودی شما باشد، مابه‌التفاوت به‌صورت خودکار
          به‌عنوان پرداخت زودهنگام در نظر گرفته می‌شود.
        </li>
        <li>
          درخواست شما به‌همراه پرداخت زودهنگام ثبت و برای تأیید به ادمین ارسال می‌شود.
        </li>
        <li>
          پس از تأیید، مبلغ به اعتبار شما اضافه و برداشت پردازش می‌شود.
        </li>
        <li>
          بازپرداخت به‌صورت خودکار از درآمد ماه بعد کانال شما کسر می‌شود.
        </li>
      </ol>

      <Divider className="my-3" orientation="right" plain>
        <span className="text-xs text-gray-500">قوانین</span>
      </Divider>

      <ul className="space-y-2 text-sm" style={{ listStyle: "none", padding: 0 }}>
        <li>
          <CheckCircleOutlined className="ml-2 text-emerald-500" />
          داشتن کانال فعال و سابقه‌ی درآمد الزامی است.
        </li>
        <li>
          <CheckCircleOutlined className="ml-2 text-emerald-500" />
          در هر زمان فقط یک پرداخت زودهنگام یا وام فعال می‌توانید داشته باشید.
        </li>
        <li>
          <BankOutlined className="ml-2 text-blue-500" />
          سقف پرداخت زودهنگام تا {percent}٪ از میانگین درآمد ماهانه‌ی شماست.
        </li>
        <li>
          <SafetyCertificateOutlined className="ml-2 text-amber-500" />
          کارمزد پرداخت زودهنگام {feePercent}٪ مبلغ پیش‌دریافت است.
        </li>
      </ul>

      {showFirstOffer && (
        <div className="mt-4 rounded-xl bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 p-3">
          <Typography.Text strong className="text-emerald-700 dark:text-emerald-400 block">
            <ThunderboltOutlined className="ml-1" />
            پیشنهاد ویژه‌ی اولین پرداخت زودهنگام
          </Typography.Text>
          <Typography.Text className="text-sm block mt-1">
            اولین پرداخت زودهنگام شما با کارمزد ویژه‌ی{" "}
            <strong>{firstFeePercent}٪</strong>
            {feePercent > (firstFeePercent ?? 0) && (
              <> به‌جای {feePercent}٪</>
            )}{" "}
            انجام می‌شود.
          </Typography.Text>
        </div>
      )}
    </div>
  );
}

type EarlyPaymentInfoModalProps = EarlyPaymentInfoProps & {
  open: boolean;
  onClose: () => void;
};

export function EarlyPaymentInfoModal({ open, onClose, ...info }: EarlyPaymentInfoModalProps) {
  return (
    <ResponsiveModal
      title="درباره‌ی پرداخت زودهنگام"
      open={open}
      onClose={onClose}
      footer={
        <Button block size="large" onClick={onClose}>
          بستن
        </Button>
      }
    >
      <EarlyPaymentInfo {...info} />
    </ResponsiveModal>
  );
}

export default EarlyPaymentInfo;
